import cx from 'classnames'
import React from 'react'

import css from './Avatar.module.scss'
import useAvatar from './hooks/useAvatar'

type Classnames = {
  container?: string
}

type Props = {
  active?: boolean
  children?: string
  classnames?: Classnames
  color?: string
  me?: boolean
  media?: string | null
  defaultMedia?: 'avatar' | 'octopus'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'sx'
  dot?: boolean
}

const Avatar: React.FC<Props> = ({
  active,
  classnames,
  children = 'A',
  color = 'blue',
  me,
  media,
  defaultMedia = 'avatar',
  size = 'md',
  dot = false,
}) => {
  const [isMediaLoaded, setIsMediaLoaded] = React.useState(false)
  const data = useAvatar(children)
  const backgroundColor = isMediaLoaded ? 'transparent' : undefined
  return (
    <div
      data-testid="user_initials"
      className={cx(
        css.containerOuter,
        classnames?.container,
        active && css.active,
        me && css.me,
        color && css[color],
        size && css[size],
        'avatar-container'
      )}
    >
      {dot && <div className={css.dot} />}
      <div className={css.container}>
        {media && (
          <img
            src={media}
            alt=""
            onLoad={() => setIsMediaLoaded(true)}
            style={{ backgroundColor }}
          />
        )}
        {!media && defaultMedia === 'octopus' && (
          <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M26.2238 18.2769H23.64C23.475 18.2769 23.3213 18.1984 23.2279 18.0669C23.1349 17.9358 23.1141 17.7687 23.1733 17.6199L24.2259 14.9734L24.2351 14.9505C24.5013 14.2444 24.6355 13.5049 24.6355 12.7502C24.6355 9.21185 21.659 6.33325 18.0001 6.33325C14.3415 6.33325 11.3645 9.21185 11.3645 12.7502C11.3645 13.1916 11.4113 13.6322 11.5029 14.0601C11.5628 14.3392 11.6447 14.6209 11.7458 14.897C11.748 14.9025 11.7497 14.9076 11.7511 14.9129L11.8236 15.0991L11.832 15.1214L12.8041 17.6233C12.8619 17.7719 12.8408 17.9383 12.747 18.0688C12.654 18.1988 12.5004 18.2768 12.336 18.2768H9.77671C8.79672 18.2768 8 19.0475 8 19.9952C8 20.9423 8.79672 21.7133 9.77671 21.7133H12.4946C12.6972 21.7133 12.8797 21.8315 12.9568 22.0128C13.0342 22.1938 12.9905 22.4023 12.8462 22.5404L10.8579 24.4441C10.5243 24.767 10.3391 25.1986 10.3391 25.6574C10.3391 26.1164 10.5243 26.5477 10.8599 26.8724C11.195 27.197 11.6416 27.3755 12.1159 27.3755C12.5906 27.3755 13.0359 27.1975 13.3702 26.8743L15.3719 24.9576C15.4673 24.8659 15.5946 24.8177 15.7234 24.8177C15.788 24.8177 15.8534 24.8297 15.9159 24.8548C16.1019 24.93 16.2229 25.1058 16.2229 25.3008V27.9483C16.2229 28.8956 17.0203 29.6666 17.9996 29.6666C18.9794 29.6666 19.7767 28.8956 19.7767 27.9483V25.3031C19.7767 25.1079 19.8977 24.9319 20.0844 24.8567C20.2703 24.7813 20.4853 24.8225 20.6284 24.96L22.6195 26.8711C22.9565 27.197 23.4028 27.3755 23.8771 27.3755C24.3515 27.3755 24.7978 27.197 25.1333 26.8724C25.4691 26.5476 25.6537 26.1166 25.6537 25.6574C25.6537 25.1986 25.4682 24.7665 25.1321 24.4411L23.1507 22.5397C23.007 22.4015 22.9639 22.1936 23.0408 22.0125C23.1182 21.8315 23.3002 21.7133 23.5028 21.7133H26.2235C27.2029 21.7133 28 20.9423 28 19.9952C28.0001 19.0476 27.2032 18.2769 26.2238 18.2769ZM16.0942 11.4083C15.2822 11.4083 14.6215 12.0471 14.6215 12.8324C14.6215 13.6176 15.2822 14.2565 16.0942 14.2565C16.906 14.2565 17.5667 13.6176 17.5667 12.8324C17.5667 12.0471 16.906 11.4083 16.0942 11.4083ZM21.2429 13.8396C20.6692 14.3946 19.7348 14.3944 19.1609 13.8396C18.5864 13.2838 18.5864 12.3804 19.1609 11.8249C19.7345 11.2703 20.6688 11.2701 21.2429 11.8252C21.4699 12.045 21.8382 12.045 22.0655 11.8252C22.2925 11.6057 22.2925 11.2497 22.0655 11.0302C21.0378 10.0366 19.3659 10.0358 18.3384 11.0299C18.2697 11.0965 18.2062 11.166 18.1468 11.238C17.6632 10.6567 16.9232 10.2834 16.0939 10.2834C14.6408 10.2834 13.4585 11.4269 13.4585 12.8322C13.4585 14.2375 14.6408 15.381 16.0939 15.381C16.9232 15.381 17.6632 15.0076 18.1468 14.4264C18.2062 14.4985 18.2696 14.5678 18.3384 14.6345C18.8521 15.1315 19.527 15.3799 20.2019 15.3799C20.8769 15.3799 21.5514 15.1315 22.0655 14.6345C22.2925 14.415 22.2925 14.059 22.0655 13.8396C21.8382 13.6197 21.4699 13.6197 21.2429 13.8396Z"
              fill="white"
            />
          </svg>
        )}
        {!media && defaultMedia !== 'octopus' && data}
      </div>
    </div>
  )
}

export default Avatar
